import { useState, useRef } from 'react';
import {
  Text, Button, Spinner, Badge, makeStyles, tokens,
} from '@fluentui/react-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { apiClient } from '../api/client';

interface UploadRecord {
  uploadId: string;
  tenantId: string;
  userId: string;
  fileName: string;
  status: 'pending' | 'success' | 'failed';
  recordsLoaded: number;
  errorsCount: number;
  uploadedAt: number;
  s3Key?: string;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '20px',
  },
  dragZone: {
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '40px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
    backgroundColor: tokens.colorNeutralBackground1,
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  dragZoneActive: {
    backgroundColor: tokens.colorBrandBackground2,
  },
  historyTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '13px',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    fontWeight: '600',
    fontSize: '12px',
  },
  td: {
    padding: '12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusBadge: {
    padding: '4px 8px',
    borderRadius: tokens.borderRadiusMedium,
    fontSize: '11px',
    fontWeight: '600',
  },
  statusSuccess: {
    backgroundColor: tokens.colorStatusSuccessBackground1,
    color: tokens.colorStatusSuccessForeground1,
  },
  statusFailed: {
    backgroundColor: tokens.colorStatusDangerBackground1,
    color: tokens.colorStatusDangerForeground1,
  },
  statusPending: {
    backgroundColor: tokens.colorStatusWarningBackground1,
    color: tokens.colorStatusWarningForeground1,
  },
  section: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: '20px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '16px',
    color: tokens.colorNeutralForeground1,
  },
});

export function InventoryUploadPage() {
  const s = useStyles();
  const t = useT();
  const { formatDate } = useLocaleSettings();
  const { user } = useAuthStore();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const tenantId = user?.tenantId ?? 'demo';

  // Fetch upload history
  const { data: uploads = [], isLoading } = useQuery({
    queryKey: ['inventory-uploads', tenantId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/inventory/uploads`);
      return res.data?.uploads ?? [];
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await apiClient.post(`/api/inventory/upload`, formData);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory-uploads'] });
      setSelectedFile(null);
    },
  });

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files?.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        setSelectedFile(file);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.currentTarget.files;
    if (files && files.length > 0) {
      setSelectedFile(files[0]);
    }
  };

  const handleUpload = async () => {
    if (selectedFile) {
      uploadMutation.mutate(selectedFile);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'success':
        return s.statusSuccess;
      case 'failed':
        return s.statusFailed;
      default:
        return s.statusPending;
    }
  };

  return (
    <div className={s.root}>
      {/* Upload Section */}
      <div className={s.section}>
        <div className={s.sectionTitle}>{t.nav.inventories}</div>
        <div
          className={`${s.dragZone} ${isDragging ? s.dragZoneActive : ''}`}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => fileInputRef.current?.click()}
        >
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📁</div>
          <Text size={500} weight="semibold">
            {t.inventoryUpload.dragDrop}
          </Text>
          <Text size={300} style={{ marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
            {t.inventoryUpload.orBrowse}
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        {selectedFile && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: tokens.borderRadiusMedium }}>
            <Text size={300}>
              <strong>{t.inventoryUpload.selected}</strong> {selectedFile.name}
            </Text>
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
              <Button
                appearance="primary"
                onClick={handleUpload}
                disabled={uploadMutation.isPending}
              >
                {uploadMutation.isPending ? <Spinner size="tiny" /> : t.inventoryUpload.upload}
              </Button>
              <Button onClick={() => setSelectedFile(null)}>{t.common.cancel}</Button>
            </div>
          </div>
        )}
      </div>

      {/* History Section */}
      <div className={s.section}>
        <div className={s.sectionTitle}>{t.inventoryUpload.historyTitle}</div>
        {isLoading ? (
          <Spinner />
        ) : uploads.length === 0 ? (
          <Text>{t.common.noData}</Text>
        ) : (
          <table className={s.historyTable}>
            <thead>
              <tr>
                <th className={s.th}>{t.inventoryUpload.colFile}</th>
                <th className={s.th}>{t.inventoryUpload.colStatus}</th>
                <th className={s.th}>{t.inventoryUpload.colRecords}</th>
                <th className={s.th}>{t.inventoryUpload.colErrors}</th>
                <th className={s.th}>{t.inventoryUpload.colDate}</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((upload: UploadRecord) => (
                <tr key={upload.uploadId}>
                  <td className={s.td}>{upload.fileName}</td>
                  <td className={s.td}>
                    <Badge
                      className={`${s.statusBadge} ${getStatusBadgeClass(upload.status)}`}
                    >
                      {upload.status}
                    </Badge>
                  </td>
                  <td className={s.td}>{upload.recordsLoaded}</td>
                  <td className={s.td}>{upload.errorsCount}</td>
                  <td className={s.td}>
                    {formatDate(new Date(upload.uploadedAt))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
